import { useEffect, useRef, useState } from "react";
import mermaid from "mermaid";
import "./MermaidDiagram.scss";

const DEFAULT_FONT_FAMILY = 'Inter, "Helvetica Neue", Arial, sans-serif';
let mermaidConfigured = false;

const resolveFontFamily = () => {
  if (typeof window === "undefined") {
    return DEFAULT_FONT_FAMILY;
  }

  const body = document.body || document.documentElement;
  if (!body) {
    return DEFAULT_FONT_FAMILY;
  }

  const computedFont = window.getComputedStyle(body).fontFamily;
  if (computedFont && computedFont !== "inherit" && computedFont !== "") {
    return computedFont;
  }

  return DEFAULT_FONT_FAMILY;
};

const MermaidDiagram = ({ chart }) => {
  const ref = useRef(null);
  const [error, setError] = useState(null);
  const [svg, setSvg] = useState(null);

  useEffect(() => {
    if (mermaidConfigured) {
      return;
    }

    const fontFamily = resolveFontFamily();

    mermaid.initialize({
      startOnLoad: false,
      theme: "default",
      securityLevel: "loose",
      fontFamily,
      fontSize: 14,
      themeVariables: {
        fontFamily,
        fontSize: "14px",
      },
      flowchart: {
        useMaxWidth: false,
        htmlLabels: true,
        curve: "basis",
        nodeSpacing: 50,
        rankSpacing: 50,
        padding: 20,
        diagramPadding: 8,
      },
      maxTextSize: 100000,
    });

    mermaidConfigured = true;
  }, []);

  useEffect(() => {
    const renderDiagram = async () => {
      if (!chart) return;
      if (!ref.current) return;

      try {
        // Trim the chart content to remove extra whitespace
        const trimmedChart = chart.trim();
        setSvg(null);
        // Generate unique ID for each render
        const id = `mermaid-${Math.random().toString(36).substr(2, 9)}`;

        // Render the mermaid diagram
        const result = await mermaid.render(
          id,
          trimmedChart,
          undefined,
          ref.current
        );

        setSvg(result.svg);
        setError(null);
      } catch (error) {
        console.error("Mermaid rendering error:", error);
        setError(error.message || "Failed to render diagram");
        setSvg(null);
      }
    };

    renderDiagram();
  }, [chart]);

  return (
    <div className="mermaid-diagram">
      {error ? (
        <pre
          style={{
            color: "#dc3545",
            backgroundColor: "#f8d7da",
            padding: "12px",
            borderRadius: "6px",
            border: "1px solid #f5c6cb",
            margin: 0,
          }}
        >
          Error rendering diagram: {error}
        </pre>
      ) : (
        <>
          {!svg && (
            <div
              className="mermaid-diagram__placeholder"
              style={{
                minHeight: "100px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <span style={{ color: "#67748e" }}>Loading diagram...</span>
            </div>
          )}
          <div
            ref={ref}
            className="mermaid-diagram__content"
            dangerouslySetInnerHTML={svg ? { __html: svg } : undefined}
          />
        </>
      )}
    </div>
  );
};

export default MermaidDiagram;
